import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Experiment {
  id: string
  name: string
  owner: string
  runs: number
  best_metric: number
  status: string
  created: string
}

interface Statistics {
  total_experiments: number
  active_experiments: number
  completed_mtd: number
  avg_runs_per_experiment: number
  best_models_promoted: number
  collaboration_score: number
}

export default function ExperimentTrackingPage() {
  const [experiments, setExperiments] = useState<Experiment[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [experimentsRes, statsRes] = await Promise.all([
        fetch('/api/experimenttracking/experiments'),
        fetch('/api/experimenttracking/statistics')
      ])
      if (experimentsRes.ok) setExperiments(await experimentsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching experiment tracking:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadge = (status: string) => {
    if (status === 'Active') return 'bg-green-100 text-green-800'
    if (status === 'Completed') return 'bg-blue-100 text-blue-800'
    return 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Experiment Tracking</h1>
          <p className="mt-2 text-gray-600">Phase 276 - ML experiment management and comparison</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Experiments</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_experiments}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Active</p>
              <p className="text-2xl font-bold text-green-600">{statistics.active_experiments}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Completed MTD</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.completed_mtd}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Runs</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.avg_runs_per_experiment}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Models Promoted</p>
              <p className="text-2xl font-bold text-orange-600">{statistics.best_models_promoted}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Collaboration</p>
              <p className="text-2xl font-bold text-green-600">{statistics.collaboration_score}%</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Experiments</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Experiment</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Owner</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Runs</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Best Metric</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {experiments.map((exp) => (
                  <tr key={exp.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{exp.name}</td>
                    <td className="px-6 py-4 text-purple-600">{exp.owner}</td>
                    <td className="px-6 py-4 text-blue-600 font-medium">{exp.runs}</td>
                    <td className="px-6 py-4 text-green-600 font-medium">{exp.best_metric}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(exp.status)}`}>{exp.status}</span>
                    </td>
                    <td className="px-6 py-4 text-gray-500">{exp.created}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  )
}
